import {
  actions as shipmentsAction,
  actions as shipmentsActions,
  selectors as shipmentSelectors,
} from 'state/shipments';
import {
  all,
  call,
  fork,
  put,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { push, replace } from 'connected-react-router';
import {
  constants as errorConstants,
  methods as errorsMethods,
} from 'utils/errors';

import moment from 'moment-timezone';

import {
  constants as dateAndTimeConstants,
  methods as dateAndTimeMethods,
  validators as dateAndTimeValidators,
} from 'utils/dateAndTimes';

import {
  constants as loadConstants,
  validators as loadValidators,
} from 'utils/loads';
import { constants as aditionalOptionsConstants } from 'utils/aditionalOptions';
import { PARTIAL_ADDRESS } from 'utils/warnings/constants';

import api from 'api';

import routes from 'utils/routes';
import keyGenerator from 'utils/keys';
import { actions as warningActions } from 'state/warnings';
import { actions as appActions, types as appActionTypes } from 'state/app';
import {
  actions as orderActions,
  actionsByType,
  selectors as orderSelectors,
  selectorsByType,
  types as orderActionTypes,
} from 'state/order';
import { selectors as settingsSelectors } from 'state/settings';
import { actions as authActions, selectors as authSelectors } from 'state/auth';
import { actions as uiActions } from 'state/ui';
import { handleGetMaximum, isMaxReached } from 'state/loads/effects';
import {
  actions as loadsActions,
  selectors as loadSelectors,
} from 'state/loads';
import {
  actions as salesIntelligenceActions,
  selectors as salesIntelligenceSelectors,
} from 'state/salesIntelligence';
import {
  actions as insuranceDirectOrderActions,
  selectors as insuranceSelectors,
} from 'state/direct-order/insurance';
import { selectors as editLogSelectors } from 'state/editLog';
import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';

import reducersUtils from 'state/utils';
import { formatters as addressFormatters } from 'utils/addresses';
import { isEmpty } from 'utils/types';
import { constants as extrasConstants } from 'utils/extras';
import { appointmentRequired, constants as orderConstants } from 'utils/order';

import { captureError } from 'sentry';
import directOrderShipmentUtils from 'state/direct-order/shipments/utils';
import { actions as errorActions } from 'state/errors';
import { calculatePriceType } from './utils';
import { initUserShipperAndCountrySettings } from 'state/app/effects';
import { getHolidaysService, getSubmitService } from './service';
import paymentConstants from 'utils/paymentData/constants';

const aditionalOptionsActions = {
  [aditionalOptionsConstants.ADITIONAL_OPTIONS.INSURANCE]: {
    [orderConstants.DIRECT_ORDER]: {
      setValue: (value) => insuranceDirectOrderActions.setInsuranceFor(value),
    },
  },
  [aditionalOptionsConstants.ADITIONAL_OPTIONS.INTERNAL_REFERENCE]: {
    [orderConstants.DIRECT_ORDER]: {
      setValue: (value, _, shipmentKey) =>
        shipmentsActions.setInternalReference({ value, shipmentKey }),
    },
    [orderConstants.DIRECT_ORDER_RECURRING]: {
      setValue: (value, _, shipmentKey) =>
        shipmentsActions.setInternalReference({ value, shipmentKey }),
    },
    [orderConstants.DISTRIBUTION]: {
      setValue: (value, _, shipmentKey) =>
        shipmentsActions.setInternalReference({ value, shipmentKey }),
    },
    [orderConstants.COLLECTION]: {
      setValue: (value, _, shipmentKey) =>
        shipmentsActions.setInternalReference({ value, shipmentKey }),
    },
  },
  [aditionalOptionsConstants.ADITIONAL_OPTIONS.COMMENT_FOR_TRANSPORTIST]: {
    [orderConstants.DIRECT_ORDER]: {
      setValue: (value, stopType) =>
        shipmentsActions.setCommentForTransportist(value, stopType),
    },
    [orderConstants.DIRECT_ORDER_RECURRING]: {
      setValue: (value, stopType) =>
        shipmentsActions.setCommentForTransportist(value, stopType),
    },
    [orderConstants.DISTRIBUTION]: {
      setValue: (value, stopType) =>
        shipmentsActions.setCommentForTransportist(value, stopType),
    },
    [orderConstants.COLLECTION]: {
      setValue: (value, stopType) =>
        shipmentsActions.setCommentForTransportist(value, stopType),
    },
  },
  [aditionalOptionsConstants.ADITIONAL_OPTIONS.APPOINTMENT_INSTRUCTIONS]: {},
};

const checkAddresses = (shipment) =>
  isEmpty(shipment.address_from) || isEmpty(shipment.address_to);

const validateRecurringDays = (days) =>
  isEmpty(days) ? { [keyGenerator.recurring.days()]: 'msg' } : {};
const validateRecurringRange = (range) => {
  const { end_on, start_from } = range;
  if (
    isEmpty(end_on) ||
    isEmpty(start_from) ||
    moment(start_from).add(7, 'day').isAfter(moment(end_on))
  ) {
    return { [keyGenerator.recurring.range()]: 'msg' };
  }
  return {};
};

const checkRecurringRange = (range) => !isEmpty(validateRecurringRange(range));

const checkRecurringDays = (days) => !isEmpty(validateRecurringDays(days));

const validateAddresses = ({ shipment, shipmentKey }) => {
  let errors = {};
  if (isEmpty(shipment.address_from))
    errors = {
      ...errors,
      [keyGenerator.address.pickup({ shipmentKey })]: 'msg',
    };
  if (isEmpty(shipment.address_to))
    errors = {
      ...errors,
      [keyGenerator.address.dropoff({ shipmentKey })]: 'msg',
    };
  if (shipment.address_from && shipment.address_from.isZipcode)
    errors = {
      ...errors,
      [keyGenerator.address.pickup({ shipmentKey })]: PARTIAL_ADDRESS,
    };
  if (shipment.address_to && shipment.address_to.isZipcode)
    errors = {
      ...errors,
      [keyGenerator.address.dropoff({ shipmentKey })]: PARTIAL_ADDRESS,
    };
  return errors;
};

const validateDateAndTimes = ({
  shipment,
  shipmentKey,
  shipments,
  orderType,
  validations,
  timeWindow,
  isEconomyModeEnabled = false,
}) => {
  const timesInShipment = dateAndTimeMethods.getTimesFromShipment(shipment);

  const maxNumberOfItemsInLoadsBetweenShipments = Object.values(
    shipments || {}
  ).reduce((max, currentShipment) => {
    const itemsPerShipment = Object.values(currentShipment.loads || {}).reduce(
      (numberOfItems, load) => numberOfItems + load.number_of_items,
      0
    );
    return itemsPerShipment > max ? itemsPerShipment : max;
  }, 0);

  const { errors } = dateAndTimeValidators.validateTimes(
    timesInShipment,
    maxNumberOfItemsInLoadsBetweenShipments,
    orderType,
    validations,
    timeWindow
  );
  const timeParams = dateAndTimeConstants.DATE_AND_TIME_KEYS;
  const errorInField = timeParams.reduce(
    (acc, timekey) =>
      dateAndTimeValidators.validateTime(shipment[timekey]).isValid
        ? { ...acc }
        : {
            ...acc,
            [timekey]: 'empty',
          },
    {}
  );
  const errorsMerged = { ...(errors || {}), ...(errorInField || {}) };

  const validationErrors = Object.keys(errorsMerged).reduce(
    (acc, errorDateTimeKey) => ({
      ...acc,
      [keyGenerator.dateTime({
        shipmentKey,
        dateTimeKey: errorDateTimeKey,
      })]: errorsMerged[errorDateTimeKey],
    }),
    {}
  );

  const validationErrorsFiltered = errorsMethods.filterTimeErrorsForEconomyMode(
    validationErrors,
    isEconomyModeEnabled
  );

  return validationErrorsFiltered;
};

const validateAditionalOptions = ({ shipment, shipmentKey, orderType }) => {
  let errors = {};
  if (shipment.dropoff_comment_from_shipper_to_driver === '')
    errors = {
      ...errors,
      [keyGenerator.aditionalOptions.dropoff({ shipmentKey })]: 'msg',
    };
  if (shipment.pickup_comment_from_shipper_to_driver === '')
    errors = {
      ...errors,
      [keyGenerator.aditionalOptions.pickup({ shipmentKey })]: 'msg',
    };
  if (shipment.internal_reference === '')
    errors = {
      ...errors,
      [keyGenerator.aditionalOptions.internal({ shipmentKey })]: 'msg',
    };

  if (
    (appointmentRequired({
      orderType,
      stopType: 'dropoff',
      poi_info: shipment?.address_to?.poi_info,
    }) &&
      !shipment?.appointment_instructions) ||
    shipment?.appointment_instructions === ''
  ) {
    errors = {
      ...errors,
      [keyGenerator.aditionalOptions.appointment({ shipmentKey })]: 'msg',
    };
  }

  return errors;
};

function* checkDateAndTimes({
  shipment,
  shipmentKey,
  shipments,
  orderType,
  validations,
}) {
  const timeWindow = yield select(
    orderSelectors.getTimeWindowByShipmentKey,
    shipmentKey
  );
  const isEconomyModeEnabled = yield select(
    orderSelectors.getIsEconomyModeEnabled
  );

  const errors = validateDateAndTimes({
    shipment,
    shipmentKey,
    shipments,
    orderType,
    validations,
    timeWindow,
    isEconomyModeEnabled,
  });

  const errorFields = {
    pickup_time_from: keyGenerator.time.from({
      shipmentKey,
      stopType: 'pickup',
    }),
    pickup_time_to: keyGenerator.time.to({
      shipmentKey,
      stopType: 'pickup',
    }),
    dropoff_time_from: keyGenerator.time.from({
      shipmentKey,
      stopType: 'dropoff',
    }),
    dropoff_time_to: keyGenerator.time.to({
      shipmentKey,
      stopType: 'dropoff',
    }),
  };

  yield put(errorActions.removeError(Object.values(errorFields)));
  const hasError = Object.keys(errors).some(
    (errorDateTimeKey) => !isEmpty(errors[errorDateTimeKey])
  );

  if (hasError) {
    for (const field of Object.keys(errorFields)) {
      const errorKey = errorFields[field];
      const error = errors[errorKey];
      if (error && !!shipment[field]) {
        yield put(
          errorActions.setError({
            [errorKey]: error,
          })
        );
      }
    }
  }
  return hasError;
}

const checkLoadsInShipment = ({
  shipment: { loads },
  maximums,
  shipmentKey,
  settings,
  validations,
  orderType,
}) => {
  if (isEmpty(loads)) return true;

  return Object.keys(loads).some((loadkey) => {
    if (!!maximums[shipmentKey]) {
      if (
        loads[loadkey][loadConstants.FIELDS.NUMBER_OF_ITEMS] >
        maximums[shipmentKey][loadkey]
      )
        return true;
    }

    const { isValid } = loadValidators.validateLoad({
      load: loads[loadkey],
      settings,
      orderType,
      validations,
    });
    return !isValid;
  });
};

const validateLoadsInShipment = ({
  shipment: { loads },
  maximums,
  shipmentKey,
  settings,
  validations,
  orderType,
}) => {
  if (isEmpty(loads))
    return {
      [keyGenerator.load({ shipmentKey, loadKey: '_', field: '_' })]: 'msg',
    };
  return Object.keys(loads).reduce((acc, loadKey) => {
    const errorInNumberOfItems =
      loads[loadKey][loadConstants.FIELDS.NUMBER_OF_ITEMS] >
      maximums[shipmentKey][loadKey]
        ? {
            [keyGenerator.load({
              shipmentKey,
              loadKey,
              field: loadConstants.FIELDS.NUMBER_OF_ITEMS,
            })]: 'msg',
          }
        : {};

    const { errors: errorsInLoad } = loadValidators.validateLoad({
      load: loads[loadKey],
      settings,
      validations,
      orderType,
    });
    const errorsInLoadFormatted = Object.keys(errorsInLoad || {}).reduce(
      (accErr, errorKey) => ({
        ...accErr,
        [keyGenerator.load({ shipmentKey, loadKey, field: errorKey })]: 'msg',
      }),
      {}
    );

    return { ...acc, ...errorInNumberOfItems, ...errorsInLoadFormatted };
  }, {});
};

function* getValuesToValidate() {
  const orderType = yield select(orderSelectors.getTypeOfOrder);
  const shipmentsWithLoads = yield select(
    orderSelectors.getShipmentsWithLoadsInForm
  );

  let maximums = yield select(orderSelectors.getMaxItemsInLoads);

  if (isEmpty(maximums)) {
    maximums = yield call(handleGetMaximum);
  }

  const settings = yield select(settingsSelectors.getCountrySettings);
  const validations = yield select(orderSelectors.getValidations);
  const isRecurring = yield select(orderSelectors.getIsRecurringOrder);
  const isEditing = yield select(orderSelectors.getIsEditing);
  const editLog = yield select(editLogSelectors.getEditLog);

  return {
    orderType,
    shipmentsWithLoads,
    maximums,
    settings,
    isRecurring,
    isEditing,
    validations,
    editLog,
  };
}

function* shouldCleanQuote() {
  const isEditing = yield select(orderSelectors.getIsEditing);
  const editLog = yield select(editLogSelectors.getEditLog);
  if (isEditing && editLog?.length <= 0) {
    return false;
  }
  return true;
}

function* validateShipmentsWithLoads(
  shipmentsWithLoads,
  { maximums, settings, validations },
  orderType
) {
  yield call(handleFetchTimeWindows);
  const shipmentsKeys = Object.keys(shipmentsWithLoads);

  for (const shipmentKey of shipmentsKeys) {
    const shipmentWithLoads = shipmentsWithLoads[shipmentKey];
    const addressWrong = yield call(checkAddresses, shipmentWithLoads);
    if (addressWrong) return true;

    const dateAndTimesWrong = yield call(checkDateAndTimes, {
      shipment: shipmentWithLoads,
      shipmentKey,
      shipments: shipmentsWithLoads,
      orderSelectors,
      orderType,
      validations,
    });
    if (dateAndTimesWrong) return true;

    const loadsWrong = yield call(checkLoadsInShipment, {
      shipment: shipmentWithLoads,
      maximums,
      shipmentKey,
      settings,
      orderType,
      validations,
    });
    if (loadsWrong) return true;
  }

  return false;
}

function* isOrderValidToFetch() {
  const { shipmentsWithLoads, maximums, settings, isRecurring, validations } =
    yield call(getValuesToValidate);
  const orderType = yield select(orderSelectors.getTypeOfOrder);

  const hasError = yield call(
    validateShipmentsWithLoads,
    shipmentsWithLoads,
    {
      maximums,
      settings,
      validations,
    },
    orderType
  );
  if (hasError) return false;

  const loads = yield select(orderSelectors.getLoadsInForm);

  const totalWeight = loads.reduce((total, load) => {
    return total + (load[loadConstants.FIELDS.TOTAL_WEIGHT] || 0);
  }, 0);

  if (totalWeight > settings.maximum_truck_weight) {
    return false;
  }

  if (isRecurring) {
    const range = yield select(selectorsByType.range[orderType].getRange);
    const days = yield select(selectorsByType.days[orderType].getDays);

    let recurringHasError = yield call(checkRecurringRange, range);
    if (recurringHasError) return false;
    recurringHasError = yield call(checkRecurringDays, days);
    if (recurringHasError) return false;
  }

  return true;
}

function* validate(options) {
  const orderType = yield select(orderSelectors.getTypeOfOrder);

  yield put(warningActions.cleanWarnings());

  const { shouldValidateAditionalOptions } = options;
  const { shipmentsWithLoads, maximums, settings, validations, isRecurring } =
    yield call(getValuesToValidate);

  let errors = {};
  let needsDefaults = [];
  let errorsAditionalOptions = {};
  const isEconomyModeEnabled = yield select(
    orderSelectors.getIsEconomyModeEnabled
  );

  const errorsInShipment = yield all([
    Object.keys(shipmentsWithLoads).reduce((acc, shipmentKey) => {
      const shipmentWithLoads = shipmentsWithLoads[shipmentKey];
      const errorsInAddresses = validateAddresses({
        shipment: shipmentWithLoads,
        shipmentKey,
      });
      const errorsDateAndTime = validateDateAndTimes({
        shipment: shipmentWithLoads,
        shipmentKey,
        shipments: shipmentsWithLoads,
        orderType,
        validations,
        isEconomyModeEnabled,
      });

      if (errorsMethods.hasPastError(errorsDateAndTime)) {
        needsDefaults = [...needsDefaults, shipmentKey];
      }

      const errorInLoads = validateLoadsInShipment({
        shipment: shipmentWithLoads,
        maximums,
        shipmentKey,
        validations,
        settings,
        orderType,
      });

      errorsAditionalOptions = {
        ...(errorsAditionalOptions || {}),
        ...(shouldValidateAditionalOptions
          ? validateAditionalOptions({
              shipment: shipmentWithLoads,
              shipmentKey,
              orderType,
            })
          : {}),
      };

      return {
        ...acc,
        ...errorsInAddresses,
        ...errorsDateAndTime,
        ...errorInLoads,
      };
    }, {}),
  ]);

  yield all([
    ...needsDefaults.map((shipmentKey) =>
      put(shipmentsActions.setDefaults({ shipmentKey }))
    ),
  ]);

  let recurringHasError = {};
  if (isRecurring) {
    const orderType = yield select(orderSelectors.getTypeOfOrder);
    const range = yield select(selectorsByType.range[orderType].getRange);
    const days = yield select(selectorsByType.days[orderType].getDays);

    const rangeErrors = yield call(validateRecurringRange, range);
    const daysErrors = yield call(validateRecurringDays, days);
    recurringHasError = { ...(rangeErrors || {}), ...(daysErrors || {}) };
  }

  errors = { ...errors, ...errorsInShipment[0], ...recurringHasError };

  return {
    isValid: false,
    errors: { ...errors },
    errorsAditionalOptions,
  };
}

function* handleQuoteError(err) {
  try {
    const orderType = yield select(orderSelectors.getTypeOfOrder);
    let errorType = errorConstants.QUOTE_ERRORS.ROUTE;
    if (
      err.errors ===
      errorConstants.QUOTE_ERROR_CODE_RESPONSES.LH_EXPRESS_VEHICLE_TYPE
    )
      errorType = errorConstants.QUOTE_ERRORS.LH_EXPRESS_VEHICLE_TYPE;
    yield put(actionsByType.quote[orderType].setQuoteError({ errorType }));
  } catch (error) {
    captureError(error);
  }
}

function* handleFetchQuote() {
  const shouldFetchQuote = yield call(isOrderValidToFetch);
  yield put(orderActions.cleanQuoteSuggestions());
  const orderType = yield select(orderSelectors.getTypeOfOrder);

  if (!shouldFetchQuote) {
    const shouldClean = yield call(shouldCleanQuote);
    if (shouldClean) {
      yield put(actionsByType.quote[orderType].cleanQuote());
    }
    return;
  }

  yield put(actionsByType.ui[orderType].setFetching(true));

  const shipperQuoteId = yield select(orderSelectors.getShipperQuoteId);
  const user = yield select(authSelectors.getUser);
  const shipmentsInForm = yield select(orderSelectors.getShipmentsForQuote);
  const extras = yield select(orderSelectors.getExtras);
  const repeatedFrom = yield select(orderSelectors.getRepeatedFrom);
  const shipperId = yield select(authSelectors.getUserShipperId);
  const country = yield select(settingsSelectors.getCountry);

  let days = {};

  const isRecurring = yield select(orderSelectors.getIsRecurringOrder);

  if (isRecurring) {
    days = yield select(selectorsByType.days[orderType].getDays);
  }

  const isEditing = yield select(orderSelectors.getIsEditing);
  const originalPrice = yield select(orderSelectors.getOriginalPrice);
  const originalId = yield select(orderSelectors.getOriginalId);

  const mainQuote = {
    user_id: user.id,
    shipper_id: shipperId,
    shipments: Object.values(shipmentsInForm),
    repeated_from: repeatedFrom,
    main_quote: true,
    extras,
    ...(isEditing && originalPrice && originalPrice.pricing_version
      ? { pricing_version: originalPrice.pricing_version }
      : {}),
    ...(originalId ? { id: originalId } : {}),
    ...(isRecurring ? { days } : {}),
    ...calculatePriceType({
      orderType,
      country,
      shipment: Object.values(shipmentsInForm)[0],
    }),
  };
  if (shipperQuoteId) {
    mainQuote.shipper_quote_id = shipperQuoteId;
  }

  const mainShipment = mainQuote.shipments[0];
  const pickupHolidays = yield select(
    orderSelectors.getHolidays,
    mainShipment.shipmentKey,
    'pickup'
  );
  const dropoffHolidays = yield select(
    orderSelectors.getHolidays,
    mainShipment.shipmentKey,
    'dropoff'
  );

  let quoteRequests = [mainQuote];
  yield put(actionsByType.quote[orderType].cleanQuoteError());

  // Add suggestion quotes in the array
  if (!isRecurring && mainQuote.price_type) {
    quoteRequests = [
      ...quoteRequests,
      ...generateSuggestionQuotes(mainQuote, {
        pickup: pickupHolidays,
        dropoff: dropoffHolidays,
      }),
    ];
  }

  const getMainQuoteFromResponse = ({ quotes }) => {
    if (!quotes) return;
    const arr = Object.values(quotes);
    return arr.find((q) => q.main_quote) || arr[0];
  };

  try {
    // Do the quote request and set the main quote
    const { data: response } = yield call(api.doQuote, quoteRequests);
    const { quotes: quotesResponse } = response;
    const quoteResponse = getMainQuoteFromResponse(response);
    let canBeEconomy = false;
    const isEconomyModeEnabled = yield select(
      orderSelectors.getIsEconomyModeEnabled
    );

    if (
      quoteResponse.available_products !== undefined &&
      quoteResponse.available_products.incompatible.length > 0
    ) {
      const incompatibleProducts =
        quoteResponse.available_products.incompatible;
      canBeEconomy =
        incompatibleProducts.length === 1 &&
        incompatibleProducts[0].product_type === 'ECONOMY' &&
        incompatibleProducts[0].info.length === 1 &&
        incompatibleProducts[0].info[0].reason === 'TIME_RESTRICTIONS';
    }
    let isEconomy =
      quoteResponse !== {} && quoteResponse.product_type === 'ECONOMY';
    // if economy mode was enabled, the new quote is not economy and it cant be economy,
    // something is wrong, disable economy mode and try to get the quote again
    if (isEconomyModeEnabled && !isEconomy && !canBeEconomy) {
      yield put(orderActions.disableEconomyMode());
      yield put(actionsByType.ui[orderType].setFetching(false));
      yield put(orderActions.fetchQuote());
      return;
    }
    yield put(actionsByType.quote[orderType].setQuote(quoteResponse));
    // SDE
    yield put(
      analyticsActions.sendEvent(
        analyticsEvents.form.priceQuoteShown({
          quoteBody: mainQuote,
          quoteResponse,
          isEditing,
          type: orderType,
        })
      )
    );

    // Read and store quote suggestions
    if (Object.keys(quotesResponse).length > 1) {
      const quoteSuggestions = Object.entries(quotesResponse)
        .filter(([type, quote]) => !quote.main_quote && quote.success)
        .map(([type, quote]) => ({
          type,
          mainQuoteRequest: mainQuote,
          quote,
          quoteRequest: quoteRequests.find(
            (qr) => qr.internal_price_type === type
          ),
        }));
      yield put(orderActions.setQuoteSuggestions({ quoteSuggestions }));
    }
  } catch (err) {
    yield captureError(err);
    yield put(actionsByType.quote[orderType].cleanQuote());
    yield fork(
      handleQuoteError,
      getMainQuoteFromResponse(err.response?.data || {})
    );
  }

  yield put(actionsByType.ui[orderType].setFetching(false));
}

/**
 * This generates the suggestion quote objects
 * */
function generateSuggestionQuotes(mainQuote, holidays) {
  const shipment = mainQuote.shipments[0];
  let suggestions = [];

  const directQuote = {
    ...mainQuote,
    price_type: orderConstants.TYPE_PRICE_DIRECT,
    internal_price_type: orderConstants.TYPE_PRICE_DIRECT,
    shipments: [
      {
        ...shipment,
        ...directOrderShipmentUtils.getDirectDates(shipment, holidays),
      },
    ],
    main_quote: false,
    x_docking_enabled: false,
  };

  if (
    mainQuote.price_type === orderConstants.TYPE_PRICE_DIRECT &&
    mainQuote.internal_price_type === orderConstants.INTERNAL_TYPE_PRICE_TODAY
  ) {
    suggestions = [...suggestions, directQuote];
  }
  return suggestions.filter((suggestion) => !!suggestion);
}

function* handleSetExtra({ payload }) {
  const { extra } = payload;
  const orderType = yield select(orderSelectors.getTypeOfOrder);
  yield put(actionsByType.extras[orderType].setExtra(extra));
  yield call(handleFetchQuote);
}

function* handleSubmitQuote() {
  const isValid = yield call(isOrderValidToFetch);

  if (!isValid) {
    return;
  }

  yield put(warningActions.cleanWarnings());

  const orderType = yield select(orderSelectors.getTypeOfOrder);
  const { errors } = yield call(validate, {
    shouldValidateAditionalOptions: false,
  });

  const errorsFiltered = {
    ...errors,
  };
  const isEconomyModeEnabled = yield select(
    orderSelectors.getIsEconomyModeEnabled
  );
  if (isEconomyModeEnabled) {
    Object.keys(errorsFiltered).forEach((errorKey) => {
      if (errorKey.endsWith('time_from') || errorKey.endsWith('time_to')) {
        delete errorsFiltered[errorKey];
      }
    });
  }

  yield put(warningActions.setWarning(errorsFiltered));
  if (isEmpty(errorsFiltered)) {
    const shipmentsInForm = yield select(
      orderSelectors.getShipmentsForCreateOrder
    );
    const shipments = Object.values(shipmentsInForm || {});
    const extras = yield select(orderSelectors.getExtras);
    const repeatedFrom = yield select(orderSelectors.getRepeatedFrom);

    let range = {};
    let days = {};

    const isRecurring = yield select(orderSelectors.getIsRecurringOrder);

    if (isRecurring) {
      range = yield select(selectorsByType.range[orderType].getRange);
      days = yield select(selectorsByType.days[orderType].getDays);
    }

    const body = {
      shipments,
      extras,
      repeated_from: repeatedFrom,
      ...(isRecurring ? { ...range } : {}),
      ...(isRecurring ? { schedule: days } : {}),
    };

    const quote = yield select(selectorsByType.quote[orderType].getQuote);
    // SDE
    yield put(
      analyticsActions.sendEvent(
        analyticsEvents.form.shipperFormSubmitted({
          type: orderType,
          body,
          quote,
        })
      )
    );

    yield put(push(routes.summary));
  }
}

// TODO SDE PAYMENT
function* handleDiscardOrder() {
  yield put(push(routes.newOrder));
  yield put({ type: reducersUtils.RESET_ORDER_STATE });
}

function* setOrderSubmitFeedbackInUI(msg) {
  yield put(uiActions.setFeedback({ feedback: msg }));
  yield call(delay, 10000);
  yield put(uiActions.setFeedback({ feedback: null }));
}

function* setOrderCreatedFeedbackInUI() {
  yield call(setOrderSubmitFeedbackInUI, 'Order:completed');
}

function* setOrderEditedFeedbackInUI() {
  yield call(setOrderSubmitFeedbackInUI, 'Order:edited');
}

function* getOrderErrors() {
  yield put(warningActions.cleanWarnings());

  const { errors, errorsAditionalOptions } = yield call(validate, {
    shouldValidateAditionalOptions: true,
  });

  const insuranceQuantity = yield select(insuranceSelectors.getInsuredQuantity);
  const errorInInsurance =
    insuranceQuantity > aditionalOptionsConstants.INSURANCE_MAX_QUANTITY;

  yield put(
    warningActions.setWarning({ ...errors, ...errorsAditionalOptions })
  );

  return {
    errors,
    errorInInsurance,
    errorAditionalOptions: !isEmpty(errorsAditionalOptions),
  };
}

function* handleConfirmOrder() {
  const orderType = yield select(orderSelectors.getTypeOfOrder);
  const country = yield select(settingsSelectors.getCountry);
  const terms_and_conditions_version = yield select(
    authSelectors.getCurrentTermsAndConditionsVersion
  );
  yield put(actionsByType.ui[orderType].setSubmitting(true));

  const { errors, errorInInsurance } = yield call(getOrderErrors);

  if (isEmpty(errors) && !errorInInsurance) {
    const shipmentsInForm = yield select(
      orderSelectors.getShipmentsForCreateOrder
    );
    const shipments = Object.values(shipmentsInForm || {});
    const extras = yield select(orderSelectors.getExtras);
    const repeatedFrom = yield select(orderSelectors.getRepeatedFrom);

    let range = {};
    let days = {};

    const isRecurring = yield select(orderSelectors.getIsRecurringOrder);

    if (isRecurring) {
      range = yield select(selectorsByType.range[orderType].getRange);
      days = yield select(selectorsByType.days[orderType].getDays);
    }

    const quote = yield select(selectorsByType.quote[orderType].getQuote);

    const body = {
      terms_and_conditions_version,
      shipments,
      extras,
      repeated_from: repeatedFrom,
      ...(isRecurring ? { ...range } : {}),
      ...(isRecurring ? { schedule: days } : {}),
      ...calculatePriceType({ orderType, country }),
      ...(quote.slug ? { quote_slug: quote.slug } : {}),
    };

    try {
      // SDE
      yield put(
        analyticsActions.sendEvent(
          analyticsEvents.form.shipperFormConfirmed({
            type: orderType,
            body,
            quote,
          })
        )
      );

      const { data: orderCreated } = yield call(
        ...getSubmitService({ isRecurring, body })
      );

      if (!isEmpty(repeatedFrom)) {
        yield put(
          analyticsActions.sendEvent(
            analyticsEvents.shipperSoRepeatSubmitted({
              type: orderType,
              shipper_order_id: repeatedFrom,
            })
          )
        );
      }

      const paymentMethod = yield select(authSelectors.getUserPaymentMethod);

      if (paymentMethod === 'CARD') {
        const { id } = orderCreated;
        const { data } = yield call(api.initPaymentIntent, id);

        yield put(
          orderActions.setPaymentIntentSecret({
            paymentIntentSecret: data?.client_secret,
          })
        );
        yield put(orderActions.setLastId({ lastId: id }));
        yield put(
          analyticsActions.sendEvent(
            analyticsEvents.form.shipperFormCard({
              type: orderType,
            })
          )
        );

        yield put(push(routes.creditcard));
      } else {
        yield put({ type: reducersUtils.RESET_ORDER_STATE });
        yield put(appActions.updateUser());

        if (!isRecurring && orderType === orderConstants.DIRECT_ORDER) {
          yield put(
            salesIntelligenceActions.fetchForRecurringOrderSuggestion({
              address_from_company_name: shipments[0].address_from.company_name,
              address_from_address_line: shipments[0].address_from.address_line,
              address_from_zipcode: shipments[0].address_from.zipcode,
              address_to_company_name: shipments[0].address_to.company_name,
              address_to_address_line: shipments[0].address_to.address_line,
              address_to_zipcode: shipments[0].address_to.zipcode,
            })
          );
        }

        if (isRecurring) {
          const user = yield select(authSelectors.getUser);
          const suggestion = yield select(
            salesIntelligenceSelectors.getRecurringOrderSuggestion
          );

          if (!isEmpty(suggestion)) {
            const {
              id: suggestionId,
              data: { address_from: addressFrom, address_to: addressTo },
            } = suggestion;
            yield put(
              analyticsActions.sendEvent(
                analyticsEvents.salesIntelligence.recurringOrderCreatedFromSuggestion(
                  { id: suggestionId, addressFrom, addressTo, user },
                  orderCreated
                )
              )
            );
            const sendEvent = false;
            yield put(
              salesIntelligenceActions.dismissRecurringOrderSuggestion({
                sendEvent,
              })
            );
          }
        }

        yield put(push(routes.orders.active));
        yield fork(setOrderCreatedFeedbackInUI);
      }
    } catch (err) {
      captureError(err);
      yield put(warningActions.setWarning({ submitOrder: 'submitOrder' }));
    }
  }
  yield put(actionsByType.ui[orderType].setSubmitting(false));
}

function* handlePayOrder({ payload }) {
  const { paymentIntent } = payload;

  const { id } = paymentIntent;
  const lastId = yield select(orderSelectors.getLastId);

  try {
    yield call(
      api.put,
      `shipper-app/shipper_orders/${lastId}/confirm_intent/`,
      {
        intent_id: id,
      }
    );

    yield put({ type: reducersUtils.RESET_ORDER_STATE });
    yield put(appActions.updateUser());
    yield put(push(routes.orders.active));
    yield fork(setOrderCreatedFeedbackInUI);
  } catch (err) {
    yield put(warningActions.setWarning({ submitOrder: 'submitOrder' }));
    captureError(err);
  } finally {
    const orderType = yield select(orderSelectors.getTypeOfOrder);

    yield put(actionsByType.ui[orderType].setSubmitting(false));
  }
}

function* handleErrorSubmitRecurringOrder() {
  yield put(
    warningActions.setWarning({ submitRecurringOrder: 'submitRecurringOrder' })
  );

  const orderType = yield select(orderSelectors.getTypeOfOrder);

  const shipmentsInForm = yield select(
    orderSelectors.getShipmentsForCreateOrder
  );
  const shipments = Object.values(shipmentsInForm || {});
  const extras = yield select(orderSelectors.getExtras);

  const range = yield select(selectorsByType.range[orderType].getRange);
  const days = yield select(selectorsByType.days[orderType].getDays);

  const quote = yield select(selectorsByType.quote[orderType].getQuote);

  const body = {
    shipments,
    extras,
    ...range,
    schedule: days,
  };

  yield put(
    analyticsActions.sendEvent(
      analyticsEvents.badPaymentMethodRecurring({
        data: body,
        quote,
      })
    )
  );
}

function* updateTermsAndConditions(payload) {
  try {
    const { acceptedTerms, commercialCommunications } = payload;
    const user = yield select(authSelectors.getUser);
    const body = {
      commercial_communications_accepted: commercialCommunications || false,
      accepted_terms: acceptedTerms || false,
    };

    yield call(api.updateUser, { userId: user.id, userData: body });
    yield put(authActions.setUser({ ...user, ...body }));
  } catch (err) {
    captureError(err);
  }
}

function* handleSubmitOrder({ payload }) {
  yield put(warningActions.cleanWarnings());

  const orderType = yield select(orderSelectors.getTypeOfOrder);
  const { acceptedTerms, commercialCommunications, needsLegalAuth } = payload;
  yield put(orderActions.setShipperQuoteId({ shipperQuoteId: null }));

  const { errors, errorInInsurance, errorAditionalOptions } = yield call(
    getOrderErrors
  );
  yield put(actionsByType.ui[orderType].setSubmitting(true));
  yield call(initUserShipperAndCountrySettings);

  if (isEmpty(errors) && !errorInInsurance && !errorAditionalOptions) {
    if (needsLegalAuth) {
      yield call(updateTermsAndConditions, {
        commercialCommunications,
        acceptedTerms,
      });
    }

    const paymentMethod = yield select(authSelectors.getUserPaymentMethod);
    const isRecurring = yield select(orderSelectors.getIsRecurringOrder);

    if (!isEmpty(paymentMethod)) {
      if (
        paymentMethod === paymentConstants.PAYMENT_METHODS.CARD &&
        isRecurring
      ) {
        yield fork(handleErrorSubmitRecurringOrder);
      } else {
        yield call(handleConfirmOrder);
      }
    } else {
      console.error('ERROR', 'ERROR TODO no payment method or CIF');
      if (isRecurring) {
        yield fork(handleErrorSubmitRecurringOrder);
      } else {
        yield put(push(routes.paymentdata));
        yield put(
          analyticsActions.sendEvent(analyticsEvents.paymentDataRequestPage())
        );
      }
    }
  }
  yield put(actionsByType.ui[orderType].setSubmitting(false));
}

function* handleSubmitNonCoreOrder(payload) {
  yield call(handleSubmitOrder, payload);
}

function* handleSubmitEditOrder() {
  const isEditing = yield select(orderSelectors.getIsEditing);
  const originalId = yield select(orderSelectors.getOriginalId);
  const orderType = yield select(orderSelectors.getTypeOfOrder);

  if (isEditing) {
    yield put(actionsByType.ui[orderType].setSubmitting(true));
    const { errors, errorInInsurance } = yield call(getOrderErrors);

    if (isEmpty(errors) && !errorInInsurance) {
      const commandsForBackend = yield select(
        editLogSelectors.getEditLogForBackend
      );
      // const shipmentsInForm = yield select(orderSelectors.getShipmentsForCreateOrder);

      const body = {
        commands: commandsForBackend,
      };

      // const quote = yield select(selectorsByType.quote[orderType].getQuote);

      try {
        // event?
        yield call(api.post, `shipper_app/orders/${originalId}/edit`, body);

        const id = yield select(orderSelectors.getOriginalId);
        const diff = yield select(orderSelectors.getDiffPriceWhenEdit);

        if (id) {
          yield put(
            analyticsActions.sendEvent(
              analyticsEvents.shipperOrderEditSubmitted({
                shipper_order_id: originalId,
                commands: commandsForBackend,
                diff,
              })
            )
          );
        }

        yield put({ type: reducersUtils.RESET_ORDER_STATE });
        yield put(appActions.updateUser());

        yield put(push(routes.orders.active));

        yield fork(setOrderEditedFeedbackInUI);
      } catch (err) {
        captureError(err);
        yield put(warningActions.setWarning({ submitOrder: 'submitOrder' }));
      }
    }
  }
  // event?
  yield put(actionsByType.ui[orderType].setSubmitting(false));
}

function* handleDiscardEditOrder() {
  try {
    const originalId = yield select(orderSelectors.getOriginalId);

    if (originalId) {
      yield put(
        analyticsActions.sendEvent(
          analyticsEvents.shipperOrderEditDiscard({
            shipper_order_id: originalId,
            place: 'SUMMARY',
          })
        )
      );
    }

    yield put({ type: reducersUtils.RESET_ORDER_STATE });
    yield put(push(routes.orders.active));
    yield put(warningActions.cleanWarnings());
  } catch (e) {
    captureError(e);
  }
}

function* handleSetComments({ payload }) {
  const { value, orderType, stopType, shipmentKey } = payload;

  const shipmentsInOrder = yield select(orderSelectors.getShipmentKeys);

  if (
    [
      orderConstants.DIRECT_ORDER,
      orderConstants.DIRECT_ORDER_RECURRING,
    ].indexOf(orderType) >= 0
  ) {
    yield all(
      shipmentsInOrder.reduce(
        (setters, shipmentId) => [
          ...setters,
          put(
            shipmentsActions.setCommentForTransportist({
              commentForTransportist: value,
              shipmentKey: shipmentId,
              stopType: stopType,
            })
          ),
        ],
        []
      )
    );
  }

  if (
    orderType === orderConstants.DISTRIBUTION ||
    orderType === orderConstants.COLLECTION
  ) {
    const filteredshipmentsInOrder = shipmentsInOrder.filter(
      (shipmentId) => shipmentId === shipmentKey
    );

    yield all(
      filteredshipmentsInOrder.map((shipmentId) =>
        put(
          shipmentsActions.setCommentForTransportist({
            commentForTransportist: value,
            shipmentKey: shipmentId,
            stopType,
          })
        )
      )
    );
  }
}

function* handleSetAditionalOption({ payload }) {
  yield put(warningActions.cleanWarnings());
  const { type, value, shipmentKey } = payload;
  const orderType = yield select(orderSelectors.getTypeOfOrder);

  if (
    type ===
    aditionalOptionsConstants.ADITIONAL_OPTIONS.COMMENT_FOR_TRANSPORTIST
  ) {
    yield call(handleSetComments, { payload: { ...payload, orderType } });
  } else {
    yield put(
      aditionalOptionsActions[type][orderType].setValue(
        value,
        orderType,
        shipmentKey
      )
    );
  }
}

function* handleCheckAditionalOption({ payload }) {
  yield put(warningActions.cleanWarnings());
  const { type, value } = payload;
  const orderType = yield select(orderSelectors.getTypeOfOrder);

  yield call(handleSetAditionalOption, { payload: { ...payload, value } });

  if (type === aditionalOptionsConstants.ADITIONAL_OPTIONS.INSURANCE)
    yield put(
      actionsByType.extras[orderType].setExtra(
        aditionalOptionsConstants.ADITIONAL_OPTIONS.INSURANCE
      )
    );
}

function* handleSetDefaults() {
  const loggedIn = yield select(authSelectors.userIsLogged);
  if (!loggedIn) {
    yield take(appActionTypes.INIT_APP_FINISHED);
  }

  const shipmentsInOrder = yield select(orderSelectors.getShipmentKeys);

  yield all([
    ...shipmentsInOrder.map((shipmentKey) =>
      put(shipmentsActions.setDefaults({ shipmentKey }))
    ),
  ]);
}

// TODO SDE PAYMENT
function* handleNavigateToForm() {
  yield put(push(routes.newOrder));
  yield fork(handleSetDefaults);
}

function* computeAddresses({ shipment }) {
  const rawFromAddress = yield call(
    addressFormatters.getAddressLabel,
    shipment.address_from
  );
  const rawToAddress = yield call(
    addressFormatters.getAddressLabel,
    shipment.address_to
  );

  return {
    address_from: { ...shipment.address_from },
    address_to: { ...shipment.address_to },
    address_from_raw: rawFromAddress,
    address_to_raw: rawToAddress,
  };
}

function* prepareAndSaveLoads({ loads, shipmentKey, keepId = false }) {
  yield all([
    ...loads.map((load, i) =>
      put(
        loadsActions.addLoad({
          shipmentKey,
          loadKey:
            keepId && load.id && String(load.id)
              ? String(load.id)
              : `LOAD-${i}-${shipmentKey}`,
          values: { ...load },
        })
      )
    ),
  ]);
  return true;
}

function* prepareAndSaveShipment({ shipment, idx, orderType, keepId = false }) {
  const newDates = yield call(
    dateAndTimeMethods.getTimesAndDatesFromShipmentCreated,
    {
      shipment,
      shouldAddDays: !keepId,
    }
  );
  const computedAddresses = yield call(computeAddresses, { shipment });

  const overrideValues = {
    pickup_comment_from_shipper_to_driver:
      shipment.pickup_comment_from_shipper_to_driver === ''
        ? false
        : shipment.pickup_comment_from_shipper_to_driver,
    dropoff_comment_from_shipper_to_driver:
      shipment.dropoff_comment_from_shipper_to_driver === ''
        ? false
        : shipment.dropoff_comment_from_shipper_to_driver,
    ...(keepId
      ? {
          internal_reference:
            shipment.internal_reference === ''
              ? false
              : shipment.internal_reference,
        }
      : {}),
    ...(keepId ? { insured_for: shipment.insured_for } : {}),
    ...newDates,
    ...computedAddresses,
    extras: (shipment.extras || []).filter(
      (extra) => extrasConstants.HANDLING_EXTRAS.indexOf(extra) >= 0
    ),
  };
  // TODO review extras filter

  const shipmentKey =
    keepId && shipment.id && !!String(shipment.id)
      ? String(shipment.id)
      : `SHIPMENT_${orderType}_${idx}_${keyGenerator.timestamp()}`;

  yield put(
    shipmentsActions.addShipment({ shipmentKey, values: { ...overrideValues } })
  );
  const { loads } = shipment;
  yield call(prepareAndSaveLoads, { loads, shipmentKey, keepId });
  return shipmentKey;
}

function* handleCheckPaymentMethod() {
  const canDoShipments = yield select(
    authSelectors.getPaymentDataAllowShipperDoShipments
  );
  if (!canDoShipments) {
    const paymentDataValidationStatus = yield select(
      authSelectors.getPaymentDataPaymentMethodValidationStatus
    );

    if (paymentDataValidationStatus === 'DENIED') {
      yield put(uiActions.togglePaymentDataDeniedModalOpened());
    } else {
      yield put(uiActions.togglePaymentDataPendingModalOpened());
    }

    yield put(replace(routes.orders.active));
    return false;
  }
  return true;
}

function* handlePrefillSuggestionOrder({ payload }) {
  const suggestions = yield select(orderSelectors.getQuoteSuggestions);
  const suggestion = suggestions.find(({ type }) => type === payload.type);

  if (!suggestion) return;

  const orderType = orderConstants.DIRECT_ORDER;
  const suggestionShipment = suggestion.quoteRequest?.shipments[0];
  const [targetShipmentKey] = yield select(
    selectorsByType.shipments[orderType].getShipmentsKeys
  );

  yield put(
    orderActions.setTypeOrder({
      type: orderType,
    })
  );
  yield put(
    shipmentsAction.changeValues({
      shipmentKey: targetShipmentKey,
      values: {
        ...directOrderShipmentUtils.formatDateTimesToPrefill(
          suggestionShipment
        ),
        // We set the proposal and original services to track the conversions
        proposalService: suggestion?.type,
        originalService: suggestion?.mainQuoteRequest?.internal_price_type,
      },
    })
  );
  yield put(
    uiActions.setFormTooltip({
      key: targetShipmentKey + '_pickup',
      content: 'suggestions.pickup-date',
      delay: 5000,
    })
  );

  yield put(orderActions.fetchQuote());
}

function* handleRepeatOrder({ payload }) {
  const canDo = yield call(handleCheckPaymentMethod);
  if (!canDo) return;

  yield put({ type: reducersUtils.RESET_ORDER_STATE });
  const { originalOrder, place } = payload;

  const { id } = originalOrder;
  let orderToRepeat = { ...originalOrder };
  try {
    const method = api.getOrder;
    const { data } = yield call(method, id);
    orderToRepeat = { ...(data || {}) };
  } catch (err) {
    captureError(err);
    return;
  }
  const { shipments } = orderToRepeat;

  const orderType = orderConstants.TYPES[orderToRepeat.journey_type];
  yield put(orderActions.setType({ type: orderType }));
  yield put(orderActions.setRecurring({ isRecurring: false }));

  const EXTRAS_IN_ORDER = ['SIDE_LOAD', 'LIFTGATE', 'TOP_LOAD'];

  const shipmentKeys = yield all([
    ...shipments.map((shipment, idx) =>
      call(prepareAndSaveShipment, {
        shipment: {
          ...shipment,
          extras: [...new Set([...shipment.extras])],
        },
        idx,
        orderType,
      })
    ),
  ]);

  // FIXME: This only works for direct order
  yield put(insuranceDirectOrderActions.cleanInsurance());

  yield put(
    actionsByType.shipments[orderType].setShipmentKeys({ shipmentKeys })
  );
  yield put(
    actionsByType.repeatedFrom[orderType].setRepeatedFrom({ orderId: id })
  );

  const extras = orderToRepeat.extras.filter(
    (extra) => EXTRAS_IN_ORDER.indexOf(extra) >= 0
  );
  yield put(actionsByType.extras[orderType].setExtras(extras));

  yield put(orderActions.setMaximums({ maximums: {} }));

  yield put(
    analyticsActions.sendEvent(
      analyticsEvents.shipperSoRepeatClicked({
        type: orderType,
        place,
        shipper_order_id: id,
      })
    )
  );
  yield call(handleNavigateToForm);
}

function* handleValidateForm() {
  yield put(loadsActions.validateForm());
  // TODO validate shipment inputs
}

function* handleSetTypeOrder({ payload }) {
  const { type } = payload;
  yield put(orderActions.setType({ type }));
  yield put(orderActions.setMaximums({ maximums: {} }));
  yield put(orderActions.cleanTimeWindows());
  yield call(isMaxReached);
  yield call(handleValidateForm);
}

function* handleSetRange({ payload }) {
  const { type, value } = payload;
  const isRecurring = yield select(orderSelectors.getIsRecurringOrder);

  if (!isRecurring) {
    return;
  }
  yield put(warningActions.cleanWarnings());
  const orderType = yield select(orderSelectors.getTypeOfOrder);

  if (type === 'start_from') {
    const valueTo = yield select(selectorsByType.range[orderType].getRangeTo);
    if (moment(value).add(7, 'day') > moment(valueTo)) {
      yield put(
        actionsByType.range[orderType].setRange({ type: 'end_on', value: '' })
      );
    }
  }
  yield put(actionsByType.range[orderType].setRange(payload));
  yield put(shipmentsActions.prepareForRecurringQuote());
  yield call(handleFetchQuote);
}

function* handleSetDay({ payload }) {
  const isRecurring = yield select(orderSelectors.getIsRecurringOrder);

  if (!isRecurring) {
    return;
  }
  yield put(warningActions.cleanWarnings());
  const orderType = yield select(orderSelectors.getTypeOfOrder);
  yield put(actionsByType.days[orderType].setDay(payload));
  yield put(shipmentsActions.prepareForRecurringQuote());
  yield call(handleFetchQuote);
}

function* handleFetchHolidays() {
  const typeOfOrder = yield select(orderSelectors.getTypeOfOrder);
  const days = yield select(selectorsByType.days[typeOfOrder].getDays);
  const start_from = yield select(
    selectorsByType.range[typeOfOrder].getRangeFrom
  );
  const end_on = yield select(selectorsByType.range[typeOfOrder].getRangeTo);
  const stops = yield select(orderSelectors.getStopsInOrder);

  const body = {
    days: days.map((day) => Number(day)).sort(),
    start_from: moment(start_from),
    end_on: moment(end_on),
    holidays_stops: stops.map((stop) => ({
      code: stop.address.outcode,
      time_from: moment(stop.time_from),
      time_to: moment(stop.time_to),
    })),
  };

  try {
    const holidays = yield call(...getHolidaysService({ body }));
    const { data } = holidays;
    if (data) {
      yield put(
        actionsByType.holidays[typeOfOrder].setHolidays({ holidays: data })
      );
    }
  } catch (err) {
    captureError(err);
  }
}

// TODO SDE PAYMENT
function* handleStartOrderWithAddress({ payload }) {
  const { address } = payload;

  yield put({ type: reducersUtils.RESET_ORDER_STATE });
  const typeOfOrder = yield select(orderSelectors.getTypeOfOrder);
  const [shipmentKey] = yield select(orderSelectors.getShipmentKeys);

  yield put(
    actionsByType.shipments[typeOfOrder].changeAddress({
      ...payload,
      stopType: dateAndTimeConstants.STOP_TYPES.dropoff,
      shipmentKey,
      address: { ...address },
    })
  );

  yield put(push(routes.newOrder));
}

const getQuoteFromPrice = (originalOrder) => {
  const {
    extra_prices = [],
    adjusted_net_price,
    vat_price,
    gross_price,
    net_full_price,
    vat_full_price,
    gross_full_price,
    pricing_version,
    is_contract = false,
    is_manual = false,
    distance_type,
    distance_at_creation,
    ignore_holidays,
    standstills = [],
    pricing_days = {
      1: null,
      2: null,
      3: null,
      4: null,
      5: null,
      6: null,
      7: null,
    },
    days = [],
  } = originalOrder;

  return {
    extra_prices,
    adjusted_net_price,
    vat_price,
    gross_price,
    net_full_price,
    vat_full_price,
    gross_full_price,
    pricing_version,
    is_contract,
    is_manual,
    distance_type,
    distance_at_creation,
    ignore_holidays,
    standstills,
    pricing_days,
    days,
  };
};

function* handleEditOrder({ payload }) {
  const canDo = yield call(handleCheckPaymentMethod);
  if (!canDo) return;
  const { originalOrder } = payload;

  const { id } = originalOrder;

  let orderToEdit = { ...originalOrder };
  try {
    const method = api.getOrder;
    const { data } = yield call(method, id);
    orderToEdit = { ...(data || {}) };
  } catch (err) {
    captureError(err);
    return;
  }
  const { shipments } = orderToEdit;
  const orderType = orderConstants.TYPES[orderToEdit.journey_type];

  yield put(orderActions.setType({ type: orderType }));
  yield put(orderActions.setRecurring({ isRecurring: false }));

  const EXTRAS_IN_ORDER = ['SIDE_LOAD', 'LIFTGATE', 'TOP_LOAD'];

  const shipmentKeys = yield all([
    ...shipments.map((shipment, idx) =>
      call(prepareAndSaveShipment, {
        shipment: {
          ...shipment,
          extras: [...new Set([...shipment.extras])],
        },
        idx,
        orderType,
        keepId: true,
      })
    ),
  ]);

  const originalPrice = yield call(getQuoteFromPrice, orderToEdit);

  yield put(
    actionsByType.shipments[orderType].setShipmentKeys({ shipmentKeys })
  );

  const extras = orderToEdit.extras.filter(
    (extra) => EXTRAS_IN_ORDER.indexOf(extra) >= 0
  );

  yield put(actionsByType.extras[orderType].setExtras(extras));
  yield put(orderActions.setMaximums({ maximums: {} }));

  yield put(
    orderActions.setEditing({
      isEditing: true,
      type: orderType,
      originalPrice,
      originalId: id,
    })
  );
  yield put(actionsByType.quote[orderType].setQuote(originalPrice));
  let isEconomy = originalOrder.product_type === 'ECONOMY';
  if (isEconomy) {
    yield put(orderActions.enableEconomyMode());
  }

  yield call(handleNavigateToForm);
}

function* handleReset() {
  yield put({ type: reducersUtils.RESET_ORDER_STATE });
}

function* handleApplyHolidays({ payload }) {
  const { address, stopType, shipmentKey } = payload;

  try {
    const { data } = yield call(api.getHolidays, {
      country: address?.country,
      zipcode: address?.zipcode,
    });
    const { holidays } = data;

    yield put(orderActions.setHolidays({ holidays, stopType, shipmentKey }));
  } catch (e) {
    captureError(e);
  }
}

/**
 * This observes the shipment and load status in order to remove the shipmentQuoteId
 * from the order when something is modified. Empty values for zipcodes are ignored.
 */
function* getStatus(shipmentKey, loadKey) {
  const orderType = yield select(orderSelectors.getTypeOfOrder);
  const pickupShipmentAddress = yield select(
    shipmentSelectors.getShipmentAddress,
    shipmentKey,
    'pickup'
  );
  const dropoffShipmentAddress = yield select(
    shipmentSelectors.getShipmentAddress,
    shipmentKey,
    'dropoff'
  );
  const height = yield select(loadSelectors.getAllHeightInLoads, shipmentKey);
  const weight = yield select(loadSelectors.getTotalWeightInLoads, shipmentKey);
  const numberOfItems = yield select(
    loadSelectors.getNumberOfItems,
    shipmentKey,
    loadKey
  );
  return {
    orderType,
    zipcodes: {
      pickup: pickupShipmentAddress?.zipcode,
      dropoff: dropoffShipmentAddress?.zipcode,
    },
    load: JSON.stringify({
      numberOfItems,
      height,
      weight,
    }),
  };
}

function* handleWatcherForShipmentQuoteId({ payload }) {
  const { shipmentKey, loadKey } = payload;
  const currentStatus = yield call(getStatus, shipmentKey, loadKey);
  let nextStatus;

  while (true) {
    yield take();
    nextStatus = yield call(getStatus, shipmentKey, loadKey);
    const orderTypeHasChanged =
      currentStatus?.orderType !== nextStatus?.orderType;
    const pickupZipcodeHasChanged =
      currentStatus?.zipcodes?.pickup !== nextStatus?.zipcodes?.pickup &&
      !!nextStatus?.zipcodes?.pickup;
    const dropoffZipcodeHasChanged =
      currentStatus?.zipcodes?.dropoff !== nextStatus?.zipcodes?.dropoff &&
      !!nextStatus?.zipcodes?.dropoff;
    const loadHasChanged = currentStatus?.load !== nextStatus?.load;
    if (
      orderTypeHasChanged ||
      pickupZipcodeHasChanged ||
      dropoffZipcodeHasChanged ||
      loadHasChanged
    ) {
      yield put(orderActions.setShipperQuoteId({ shipperQuoteId: null }));
      return;
    }
  }
}

const prepareShipmentsForGetTimeWindows = (shipments) => {
  const getAddressData = (address) => ({
    latitude: address.latitude,
    longitude: address.longitude,
    zipcode: address.zipcode,
    country_code: address.country,
  });

  return shipments
    .map((shipment) => {
      const addressFrom = shipment?.address_from;
      const addressTo = shipment?.address_to;

      if (!addressFrom || !addressTo) {
        return null;
      }

      return {
        address_from: getAddressData(shipment.address_from),
        address_to: getAddressData(shipment.address_to),
        pickup_time_from: moment(shipment.pickup_time_from).format(),
        pickup_time_to: moment(shipment.pickup_time_to).format(),
        load: {
          number_of_items:
            shipment.loads.reduce((acc, cur) => {
              let correctedNumberOfItems = cur.number_of_items;
              if (
                correctedNumberOfItems === '' ||
                correctedNumberOfItems === undefined
              ) {
                correctedNumberOfItems = 1;
              }
              return acc + correctedNumberOfItems;
            }, 0) || 1,
        },
      };
    })
    .filter((shipment) => !!shipment);
};

function* handleFetchTimeWindows() {
  const url = window.location.href;
  const skipFecthTimeWindows = url.includes(routes.newOrderCatalog);
  if (skipFecthTimeWindows) {
    return;
  }

  const shipmentsInForm = yield select(orderSelectors.getShipmentsForQuote);
  const shipments = prepareShipmentsForGetTimeWindows(
    Object.values(shipmentsInForm)
  );
  const timeWindowsRequestPayload = {
    shipments: shipments,
    request_time: moment().format(),
  };

  const prevRequest = yield select(orderSelectors.getTimeWindowsRequestPayload);

  // filter same requests because it's not necessary
  if (
    shipments.length <= 0 ||
    JSON.stringify(prevRequest?.shipments) === JSON.stringify(shipments)
  ) {
    return;
  }

  try {
    yield put(
      orderActions.setTimeWindowsRequestPayload({
        timeWindowsRequestPayload,
      })
    );
    const { data: timeWindows } = yield call(
      api.getTimeWindows,
      timeWindowsRequestPayload
    );
    yield put(
      orderActions.setTimeWindows({
        timeWindows,
      })
    );
  } catch (err) {
    console.error('Error fetching window times', err);
  }
}

export default function* () {
  yield takeLatest(orderActionTypes.setTypeOrder$, handleSetTypeOrder);
  yield takeLatest(orderActionTypes.fetchQuote$, handleFetchQuote);
  yield takeLatest(orderActionTypes.setExtra$, handleSetExtra);

  // from:V - summary:V
  yield takeLatest(orderActionTypes.submitQuote$, handleSubmitQuote);

  // summary:V - CARD ? payment:V  : confirm$
  yield takeLatest(orderActionTypes.submitOrder$, handleSubmitOrder);
  yield takeLatest(orderActionTypes.submitEditOrder$, handleSubmitEditOrder);
  yield takeLatest(
    orderActionTypes.submitNonCoreOrder$,
    handleSubmitNonCoreOrder
  );
  yield takeLatest(orderActionTypes.discardEdition$, handleDiscardEditOrder);
  // pay - confirm$
  yield takeLatest(orderActionTypes.payOrder$, handlePayOrder);
  yield takeLatest(orderActionTypes.confirmOrder$, handleConfirmOrder);

  yield takeLatest(
    orderActionTypes.checkAditionalOption$,
    handleCheckAditionalOption
  );
  yield takeLatest(
    orderActionTypes.setAditionalOption$,
    handleSetAditionalOption
  );

  yield takeLatest(orderActionTypes.setDefaults$, handleSetDefaults);

  yield takeLatest(orderActionTypes.navigateToForm$, handleNavigateToForm);
  yield takeLatest(orderActionTypes.discardOrder$, handleDiscardOrder);

  yield takeLatest(orderActionTypes.repeatOrder$, handleRepeatOrder);
  yield takeLatest(
    orderActionTypes.prefillSuggestedOrder$,
    handlePrefillSuggestionOrder
  );

  yield takeLatest(orderActionTypes.setRange$, handleSetRange);
  yield takeLatest(orderActionTypes.setDay$, handleSetDay);

  yield takeLatest(orderActionTypes.fetchHolidays$, handleFetchHolidays);

  yield takeLatest(
    orderActionTypes.startOrderWithAddress$,
    handleStartOrderWithAddress
  );

  yield takeLatest(
    orderActionTypes.checkPaymentMethod$,
    handleCheckPaymentMethod
  );

  yield takeLatest(orderActionTypes.editOrder$, handleEditOrder);

  yield takeLatest(orderActionTypes.cleanRepeatAndEditing$, handleReset);

  yield takeLatest(orderActionTypes.applyHolidays$, handleApplyHolidays);
  yield takeLatest(
    orderActionTypes.watchForShipperQuoteId$,
    handleWatcherForShipmentQuoteId
  );
  yield takeLatest(orderActionTypes.fetchTimeWindows$, handleFetchTimeWindows);
}

export {
  handleSetTypeOrder,
  handleFetchQuote,
  isOrderValidToFetch,
  getValuesToValidate,
  shouldCleanQuote,
  handleQuoteError,
  handleConfirmOrder,
  handlePrefillSuggestionOrder,
  handleNavigateToForm,
  validateShipmentsWithLoads,
  handleValidateForm,
  generateSuggestionQuotes,
  handleWatcherForShipmentQuoteId,
  getStatus,
  handleSubmitOrder,
  handleSubmitNonCoreOrder,
  getOrderErrors,
  handleFetchTimeWindows,
  validateDateAndTimes,
};
